import { Box, CheckboxLabel, dateFormatter, IUserAccount, MenuItem, Select, Skeleton, Tab, Tabs, TextField } from '@frontend-workspace/react-shared-lib';
import { useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useGetRequestNewHook } from '../requestUtils';
import { ISession } from '../_models/active-user';
import { ICompany } from '../_models/company';
import { ISessionDetails } from '../_models/session-details';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface SessionDetailsProps {
  sessionId: string;
}

export function SessionDetails(props: SessionDetailsProps) {
  const { t } = useTranslation();
  const { data: details }: { data: ISessionDetails } = useGetRequestNewHook(`licenses/sessionDetails/${props.sessionId}`, undefined);
  const session = details?.session;

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);
  const navigateToTabThree = () => setSelectedTab(2);
  const navigateToTabFour = () => setSelectedTab(3);
  const navigateToTabFive = () => setSelectedTab(4);
  const navigateToTabSix = () => setSelectedTab(5);
  const navigateToTabSeven = () => setSelectedTab(6);

  return <>
    <Box className="p-4">
      {!session ? <Skeleton variant="rectangular" animation="wave" width='100%' height={400} /> :
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
            <Tabs
              value={selectedTab}
              variant="scrollable"
              scrollButtons="auto">
              <Tab label={t("sessionDetails.details")} onClick={navigateToTabOne} value={0} />
              {session.seats.length > 1 &&
                <Tab label={t("standard.modules")} onClick={navigateToTabTwo} value={1} />
              }
              <Tab label={t("standard.machine")} onClick={navigateToTabThree} value={2} />
              <Tab label={t("standard.loan")} onClick={navigateToTabFour} value={3} />
              <Tab label={t("standard.account")} onClick={navigateToTabFive} value={4} />
              <Tab label={t("standard.person")} onClick={navigateToTabSix} value={5} />
              <Tab label={t("standard.company")} onClick={navigateToTabSeven} value={6} />
            </Tabs>
          </Box>
          {selectedTab === 0 && <Details session={session} />}
          {selectedTab === 1 && <Modules session={session} />}
          {selectedTab === 2 && <Machine session={session} />}
          {selectedTab === 3 && <Loan session={session} />}
          {selectedTab === 4 && <Account session={session} account={details.account} />}
          {selectedTab === 5 && <Person session={session} account={details.account} />}
          {selectedTab === 6 && <Company company={details.company} />}
        </>}
    </Box>
  </>;
}

function Modules(props: {
  session: ISession
}) {
  const { t } = useTranslation();
  const modules = props.session.seats.map(s => {
    return <Col xl={2}>
      <div>
        <TextField
          value={s.productName}
          label={t("standard.productName")}
          disabled
          className='mb-3'
        />
      </div>
      <div>
        <TextField
          value={s.licenseNumber}
          label={t("standard.licenseNumber")}
          disabled
          className='mb-3'
        />
      </div>
      <div>
        <TextField
          value={s.licensingModel}
          label={t("sessionDetails.licenseType")}
          disabled
          className='mb-3'
        />
      </div>
      <div>
        <TextField
          value={s.maxSeats}
          label={t("standard.maxNumberOfSeats")}
          disabled
          className='mb-3'
        />
      </div>
    </Col>
  })
  return <Row>
    {modules}
  </Row>
}

function Company(props: {
  company: ICompany
}) {
  const { t } = useTranslation();
  const company = props.company;
  return (
    <Row>
      <Col md={8} xxl={4}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={company.companyName}
            fullWidth
            label={t("standard.name")}
            disabled
            className='mb-3'
          />
          <TextField
            value={company.department}
            fullWidth
            label={t("standard.department")}
            disabled
            className='mb-3'
          />
          <TextField
            value={company.id || ''}
            fullWidth
            label={t("standard.companyID")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col xxl={4}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={company.companyNumber || ''}
            fullWidth
            label={t("standard.number")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={4} xxl={4}>
        <div className="d-flex flex-column mb-3">
          <CheckboxLabel
            label={t("standard.academic")}
            checked={company.companyType === "1"}
            disabled={true}
          />
          <CheckboxLabel
            label={t("standard.blocked")}
            checked={company.isBlocked}
            disabled={true}
          />
        </div>
      </Col>
    </Row>
  );
}

function Person(props: {
  session: ISession,
  account: IUserAccount
}) {
  const { t } = useTranslation();
  return (
    <Row>
      <Col md={8} xxl={6}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={props.account?.firstName || ''}
            fullWidth
            label={t("standard.firstName")}
            disabled
            className='mb-3'
          />
          <TextField
            value={props.account?.lastName || ''}
            fullWidth
            label={t("standard.lastName")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={4} xxl={6}>
        <div className="d-flex flex-column mb-3">
          <CheckboxLabel
            label={t("standard.retired")}
            checked={props.account?.isRetired}
            disabled={true}
          />
          <CheckboxLabel
            label={t("standard.blocked")}
            checked={props.account?.isBlocked}
            disabled={true}
          />
        </div>
      </Col>
    </Row>
  );
}

function Account(props: {
  session: ISession,
  account: IUserAccount
}) {
  const { t } = useTranslation();
  const session = props.session;
  return (
    <Row>
      <Col md={8} xxl={6}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={session.userEmail}
            fullWidth
            label={t("standard.email")}
            disabled
            className='mb-3'
          />
          <TextField
            value={session.userId}
            fullWidth
            label={t("standard.accountID")}
            disabled
            className='mb-3' />
        </div>
      </Col>
      <Col md={4} xxl={6}>
        <div className="d-flex flex-column mb-3">
          <CheckboxLabel
            label={t("standard.blocked")}
            checked={props.account.isBlocked}
            disabled={true}
          />
          <CheckboxLabel
            label={t("standard.student")}
            checked={props.account.isAcademic}
            disabled={true}
          />
          <CheckboxLabel
            label={t("sessionDetails.loggedInToMyPage")}
            checked={props.account.isActivated}
            disabled={true}
          />
        </div>
      </Col>
    </Row>
  );
}

function Loan(props: { session: ISession }) {
  const { t } = useTranslation();
  const session = props.session;
  const createdTimeDate = new Date(session.created);
  const now = new Date();
  const logonTime = Math.floor((+now - +createdTimeDate) / 1000);
  return (
    <Row>
      <Col md={6} xxl={3}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={session.id}
            fullWidth
            label={t("standard.loanId")}
            disabled
            className='mb-3'
          />
          <TextField
            value={dateFormatter(session.created)}
            fullWidth
            label={t("standard.created")}
            disabled
            className='mb-3'
          />
          <TextField
            value={dateFormatter(session.expires)}
            fullWidth
            label={t("standard.expires")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={6} xxl={3}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={dateFormatter(session.renewed)}
            fullWidth
            label={t("standard.lastRenewed")}
            disabled
            className='mb-3'
          />
          <TextField
            value={logonTime}
            fullWidth
            label={t("sessionDetails.loginTimeInSeconds")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={6} xxl={3}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={'Mangler data'}
            fullWidth
            label={t("standard.returnType")}
            disabled
            className='mb-3'
          />
          <TextField
            value={session.closedBy || t("standard.notReleased")}
            fullWidth
            label={t("standard.releasedBy")}
            disabled
            className='mb-3'
          />
          <TextField
            value={dateFormatter(session?.closed)}
            fullWidth
            label={t("standard.returned")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
    </Row>
  );
}

function Details(props: { session: ISession }) {
  const { t } = useTranslation();
  const isActive = (p: any) => !p.hasClosed;
  const processes = props.session.processes;
  const activeProcesses = processes?.filter(isActive);
  const [selectedProcessInfoId, setSelectedProcessInfoId] = useState(activeProcesses != null && activeProcesses.length > 0 ? activeProcesses[0].id : null);

  const selectedProcessInfo = processes?.find(p => p.id === selectedProcessInfoId);
  const handleSelectProcessInfo = (event: any) => setSelectedProcessInfoId(event.target.value);

  return (<>
    <div className="d-inline-flex">
      <Select
        label={`${t("standard.processes")} (${processes.filter(isActive).length}/${processes.length})`}
        value={selectedProcessInfoId || ''}
        onChange={handleSelectProcessInfo}
        className='mb-3'>
        {processes?.map((c) => <MenuItem value={c.id} key={c.id}>{`${c.processId} ${isActive(c) ? "" : t("standard.terminated")}`}</MenuItem>)}
      </Select>
    </div>
    <Row>
      <Col md={4} xxl={3}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={props.session.appName}
            fullWidth
            label={t("standard.mainProduct")}
            disabled
            className='mb-3'
          />
          <TextField
            value={props.session.licenseNumber}
            fullWidth
            label={t("standard.mainLicenseNo")}
            disabled
            className='mb-3'
          />
          <TextField
            value={props.session.containerName}
            fullWidth
            label={t("sessionDetails.containerProduct")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={4} xxl={2}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={props.session.licensingModel}
            fullWidth
            label={t("standard.licenseModel")}
            disabled
            className='mb-3'
          />
          <TextField
            value={props.session.maxSeats}
            fullWidth
            label={t("standard.maxSeat")}
            disabled
            className='mb-3'
          />
          <TextField
            value={props.session.containerVersion}
            fullWidth
            label={t("sessionDetails.containerVersion")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={4} xxl={3}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={selectedProcessInfo?.name || ''}
            fullWidth
            label={t("standard.fileName")}
            disabled
            className='mb-3'
          />
          <TextField
            value={selectedProcessInfo?.version || ''}
            fullWidth
            label={t("standard.fileVersion")}
            disabled
            className='mb-3'
          />
          <TextField
            value={selectedProcessInfo?.processId || ''}
            fullWidth
            label={t("sessionDetails.processID")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={4} xxl={2}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={dateFormatter(selectedProcessInfo?.startTime)}
            fullWidth
            label={t("standard.started")}
            disabled
            className='mb-3'
          />
          <TextField
            value={dateFormatter(selectedProcessInfo?.closed)}
            fullWidth
            label={t("standard.stopped")}
            disabled
            className='mb-3'
          />
          <TextField
            value={selectedProcessInfo?.sessionId || ''}
            fullWidth
            label={t("standard.sessionID")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={2} xxl={1}>
        <div className="d-flex flex-column mb-3">
          <CheckboxLabel
            label={t("standard.academic")}
            checked={props.session.isAcademic}
            disabled={true} />
        </div>
      </Col>
    </Row>
  </>);
}

function Machine(props: { session: ISession }) {
  const { t } = useTranslation();
  const session = props.session;
  const firstMacAddresses = session.macAddresses?.slice(0, 3);
  return (
    <Row>
      <Col md={6} xxl={3}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={session.deviceUser || ''}
            fullWidth
            label={t("sessionDetails.windowsUser")}
            disabled
            className='mb-3'
          />
          <TextField
            value={session.domainName || ''}
            fullWidth
            label={t("standard.domain")}
            disabled
            className='mb-3'
          />
          <TextField
            value={session.machineName || ''}
            fullWidth
            label={t("standard.machine")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={6} xxl={3}>
        <div className="d-flex flex-column mb-3">
          <TextField
            value={session.systemVolumeSerialNumber}
            fullWidth
            label={t("standard.volumeID")}
            disabled
            className='mb-3'
          />
          <TextField
            value={session.osPlatform}
            fullWidth
            label={t("standard.operatingSystem")}
            disabled
            className='mb-3'
          />
        </div>
      </Col>
      <Col md={6} xxl={3} title={session.macAddresses?.join('\n')}>
        <div className="d-flex flex-column mb-3" >
          <label>{t("standard.macAddresses")}:</label>
          {firstMacAddresses.map(address =>
            <TextField
              value={address || ''}
              fullWidth
              disabled
              key={address}
              className='mb-3'
            />
          )}
        </div>
      </Col>
      <Col md={6} xxl={3}>
        <div className="d-flex flex-column mb-3">
          <CheckboxLabel
            label={t("standard.virtualMachine")}
            checked={session.isVirtualEnvironment}
            disabled={true}
          />
        </div>
      </Col>
    </Row>
  );
}

export default SessionDetails;